import { createSignal, createEffect, Show, For, createMemo } from 'solid-js';
import { useApi, isOk } from '../api';
import { useTenantPermission } from '../hooks/tenantPermissions';
import { useUserContext } from '../components/UserContextProvider';
import { TenantMembership, TenantRole } from '../models/Tenant';
import { useToast } from '../components/MessageBox';
import { A, useNavigate } from '@solidjs/router';
import Text from '../components/Text';
import StateDisplay from '../components/StateDisplay';
import { showConfirm } from '../components/Dialog';
import { useTranslation } from '../hooks/useTranslation';
import { useRoleParser } from '../hooks/permissions';
import styles from './Tenants.module.css';
import membersStyles from './TenantMembers.module.css';

export default function TenantMembers() {
    const api = useApi();
    const toast = useToast();
    const navigate = useNavigate();
    const { userTenant, setUserTenant, user } = useUserContext();
    const activeTenantId = () => userTenant()?.tenantId ?? null;
    const { hasRole } = useTenantPermission();
    const { t } = useTranslation();
    const { parseRoles, roles } = useRoleParser();
    const isPlatformAdmin = () => parseRoles(user()?.permissions ?? 0, roles)[roles.indexOf("Root")] && user()?.showAdminUiInMemberApp === true;

    const [members, setMembers] = createSignal<TenantMembership[]>([]);
    const [loadingMembers, setLoadingMembers] = createSignal(true);

    async function loadMembers() {
        const id = activeTenantId();
        if (!id) return;
        setLoadingMembers(true);
        try {
            const res = await api.tenant.getMembers(id);
            if (isOk(res.data)) setMembers((res.data.value.members ?? []) as TenantMembership[]);
        } finally {
            setLoadingMembers(false);
        }
    }

    createEffect(loadMembers);

    async function removeMember(userId: string) {
        const id = activeTenantId();
        if (!id) return;
        if (!await showConfirm(t('ADMIN_TENANT_DELETE_CONFIRM'), true)) return;
        const res = await api.tenant.removeMember(id, userId);
        if (isOk(res.data)) {
            toast?.info(t('TENANT_MEMBER_REMOVED'));
            await loadMembers();
        }
    }

    async function changeRole(userId: string, role: TenantRole) {
        const id = activeTenantId();
        if (!id) return;
        const res = await api.tenant.updateMemberRole(id, userId, { role });
        if (isOk(res.data)) {
            toast?.info(t('TENANT_ROLE_UPDATED'));
            await loadMembers();
        }
    }

    async function leaveTeam() {
        const id = activeTenantId();
        if (!id) return;
        if (!await showConfirm(t('TENANT_LEAVE_CONFIRM'))) return;
        const res = await api.tenant.leaveTenant(id);
        if (isOk(res.data)) {
            toast?.info(t('TENANT_LEFT'));
            setUserTenant(null);
            navigate('/dashboard');
        }
    }

    function initial(name?: string) {
        return (name ?? '?').trim().slice(0, 1).toUpperCase();
    }

    function roleBadgeClass(role: TenantRole) {
        switch (role) {
            case TenantRole.Owner: return membersStyles.roleBadgeOwner;
            case TenantRole.Admin: return membersStyles.roleBadgeAdmin;
            default: return membersStyles.roleBadgeMember;
        }
    }

    const canManage = createMemo(() => hasRole(TenantRole.Admin) || isPlatformAdmin());

    return (
        <div class={styles.container}>
            <Show
                when={activeTenantId()}
                fallback={<StateDisplay empty icon="fa-solid fa-users-slash" message="TENANT_NO_SELECTION" />}
            >
                <div class={styles.header}>
                    <div class={styles.titleSection}>
                        <div class={styles.cardAvatar}>
                            <i class="fa-solid fa-users"></i>
                        </div>
                        <div class={styles.titleText}>
                            <h1>{userTenant()?.tenant?.name ?? t('TENANT_MEMBERS')}</h1>
                            <Show when={userTenant()?.tenant?.slug}>
                                <p class={styles.subtitle}>@{userTenant()?.tenant?.slug}</p>
                            </Show>
                        </div>
                    </div>
                    <div class={styles.headerActions}>
                        <button class="btn btn-danger" onClick={leaveTeam}>
                            <i class="fa-solid fa-right-from-bracket"></i>
                            <Text val="TENANT_LEAVE" />
                        </button>
                    </div>
                </div>

                <div class={styles.section}>
                    <div class={styles.sectionHeader}>
                        <h2><Text val="TENANT_MEMBERS_LIST" /></h2>
                        <div class="h" style={{ gap: 'var(--spacing-sm)', 'align-items': 'center' }}>
                            <span class={membersStyles.sectionBadge}>
                                <i class="fa-solid fa-hashtag"></i>
                                {members().length}
                            </span>
                            <Show when={canManage()}>
                                <A href="/tenant/members/new" class="btn btn-primary btn-sm">
                                    <i class="fa-solid fa-plus"></i>
                                    <Text val="TENANT_ADD_MEMBER" />
                                </A>
                            </Show>
                        </div>
                    </div>

                    <Show when={loadingMembers()}>
                        <StateDisplay loading />
                    </Show>
                    <Show when={!loadingMembers() && members().length === 0}>
                        <StateDisplay empty icon="fa-solid fa-users" message="TENANT_NO_MEMBERS" helpText="TENANT_MEMBERS_EMPTY_HELP" />
                    </Show>
                    <Show when={!loadingMembers() && members().length > 0}>
                        <div class={membersStyles.grid}>
                            <For each={members()}>
                                {(m) => {
                                    const isSelf = m.userId === user()?.id;
                                    const username = m.username ?? m.user?.username ?? m.userId;
                                    return (
                                        <div class={`${membersStyles.memberCard}${isSelf ? ` ${membersStyles.isSelf}` : ''}`}>
                                            <div class={membersStyles.memberHead}>
                                                <div class={membersStyles.memberAvatar}>{initial(username)}</div>
                                                <div class={membersStyles.memberInfo}>
                                                    <div class={membersStyles.memberName}>
                                                        {username}
                                                        {isSelf ? <span class={membersStyles.selfTag}>you</span> : null}
                                                    </div>
                                                    <span class={`${membersStyles.roleBadge} ${roleBadgeClass(m.role)}`}>
                                                        <Show when={m.role === TenantRole.Owner}><i class="fa-solid fa-crown"></i></Show>
                                                        <Show when={m.role === TenantRole.Admin}><i class="fa-solid fa-user-shield"></i></Show>
                                                        <Show when={m.role === TenantRole.Member}><i class="fa-solid fa-user"></i></Show>
                                                        {TenantRole[m.role]}
                                                    </span>
                                                </div>
                                            </div>
                                            <Show when={canManage()}>
                                                <div class={membersStyles.memberActions}>
                                                    <select
                                                        class={membersStyles.memberRoleSelect}
                                                        value={m.role}
                                                        onChange={e => changeRole(m.userId, parseInt(e.currentTarget.value) as TenantRole)}
                                                    >
                                                        <option value={TenantRole.Member}>{t('TENANT_ROLE_MEMBER')}</option>
                                                        <option value={TenantRole.Admin}>{t('TENANT_ROLE_ADMIN')}</option>
                                                        <option value={TenantRole.Owner}>{t('TENANT_ROLE_OWNER')}</option>
                                                    </select>
                                                    <Show when={!isSelf}>
                                                        <button
                                                            class="btn btn-danger btn-icon"
                                                            onClick={() => removeMember(m.userId)}
                                                            title={t('TENANT_REMOVE')}
                                                        >
                                                            <i class="fa-solid fa-trash"></i>
                                                        </button>
                                                    </Show>
                                                </div>
                                            </Show>
                                        </div>
                                    );
                                }}
                            </For>
                        </div>
                    </Show>
                </div>
            </Show>
        </div>
    );
}
